import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { useStudioMembers, useStudioAppointments } from '@/features/studio/hooks/useStudio';
import { MemberCard } from '@/features/studio/components/MemberCard';
import { InviteDialog } from '@/features/studio/components/InviteDialog';
import { motion } from 'framer-motion';
import { Building2, UserPlus, Users, CalendarDays, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function StudioDashboard() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { membership } = useStudio();
  const { data: members = [], isLoading: membersLoading } = useStudioMembers();
  const { data: appointments = [], isLoading: apptLoading } = useStudioAppointments();
  const [inviteOpen, setInviteOpen] = useState(false);

  if (!membership?.isOwner) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>Accès réservé au propriétaire du studio.</p>
      </div>
    );
  }

  const activeMembers = members.filter(m => m.status === 'active');
  const archivedMembers = members.filter(m => m.status === 'archived');

  return (
    <div className="tablet-container pb-28 space-y-6">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 pt-6">
        <button onClick={() => navigate('/')} className="touch-button p-3 bg-secondary rounded-xl">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-primary" />
            <h1 className="section-title">{membership.studioName}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeMembers.length} {language === 'fr' ? 'artiste(s) actif(s)' : 'active artist(s)'}
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm"
        >
          <UserPlus size={16} />
          {language === 'fr' ? 'Inviter' : 'Invite'}
        </button>
      </motion.div>

      <section className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <Users size={16} className="text-primary" />
          {language === 'fr' ? 'Artistes actifs' : 'Active artists'} ({activeMembers.length})
        </h2>
        {membersLoading ? (
          <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-16 rounded-2xl bg-secondary/40 animate-pulse" />)}</div>
        ) : activeMembers.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <p className="text-4xl mb-3">🎨</p>
            <p className="text-sm">{language === 'fr' ? "Aucun artiste — invitez votre équipe." : "No artists yet — invite your team."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeMembers.map(m => (
              <MemberCard key={m.id} member={m} onViewProfile={userId => navigate(`/studio/members/${userId}`)} />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <CalendarDays size={16} className="text-primary" />
          {language === 'fr' ? 'Agenda du studio (à venir)' : 'Studio agenda (upcoming)'}
        </h2>
        {apptLoading ? (
          <div className="h-20 rounded-2xl bg-secondary/40 animate-pulse" />
        ) : appointments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {language === 'fr' ? 'Aucun rendez-vous à venir.' : 'No upcoming appointments.'}
          </p>
        ) : (
          <div className="space-y-2">
            {(appointments as any[]).slice(0, 10).map((a) => (
              <div key={a.id} className="card-elevated p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{a.client_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(a.date), 'd MMM', { locale: fr })} · {a.start_time.slice(0, 5)} · {a.artist_name}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg font-medium ${
                  a.status === 'cancelled' ? 'bg-destructive/10 text-destructive' :
                  a.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' :
                  'bg-primary/10 text-primary'
                }`}>{a.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {archivedMembers.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-muted-foreground text-sm">
            {language === 'fr' ? 'Membres suspendus' : 'Suspended'} ({archivedMembers.length})
          </h2>
          <div className="space-y-2">
            {archivedMembers.map(m => (
              <MemberCard key={m.id} member={m} onViewProfile={userId => navigate(`/studio/members/${userId}`)} />
            ))}
          </div>
        </section>
      )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
